import { GenNode } from '../types/carbon/models/GenNode'

let nextNodeId = 10000000

export function generateNodeId(): number {
  return ++nextNodeId
}

/**
 * Converts a Variable node (with selected codes) into a Codeframe node suitable for axis display
 */
export function convertVariableToCodeframe(variableNode: GenNode, selectedCodes: GenNode[] = []): GenNode {
  // If no codes are selected, include all codes from the variable
  const codesToInclude = selectedCodes.length > 0 ? selectedCodes : (variableNode.children || [])
  
  // Generate the parent ID first so children can reference it
  const codeframeId = generateNodeId()
  const children = codesToInclude
    .filter(code => code.type === 'Code' || code.type === 'Net' || code.type === 'Arith')
    .map(code => ({
      ...code,
      id: generateNodeId(),
      parentId: codeframeId,
      level: 2
    }))

  let value2 = (variableNode.value2 || variableNode.value1 || '') as string

  (variableNode.meta || []).forEach(meta => {
    if(meta.key === 'question') {
      value2 = meta.value || ''
    }
  })

  return {
    type: 'Codeframe',
    id: codeframeId,
    parentId: 0,
    isExpanded: true,
    isFolder: true,
    value1: variableNode.value1, // Variable name becomes codeframe name
    value2,
    level: 1,
    anyChildren: children.length > 0,
    children,
  }
}

/**
 * Reorders nodes in an array by moving a node up or down
 */
export function reorderNodes(nodes: GenNode[], nodeIndex: number, direction: 'up' | 'down' | 'top' | 'bottom'): GenNode[] {
  if (nodeIndex < 0 || nodeIndex >= nodes.length) {
    return nodes
  }

  const newNodes = [...nodes]
  const [nodeToMove] = newNodes.splice(nodeIndex, 1)

  switch (direction) {
    case 'up':
      if (nodeIndex > 0) {
        newNodes.splice(nodeIndex - 1, 0, nodeToMove)
      } else {
        newNodes.push(nodeToMove) // Move to end if at beginning
      }
      break
    case 'down':
      if (nodeIndex < nodes.length - 1) {
        newNodes.splice(nodeIndex + 1, 0, nodeToMove)
      } else {
        newNodes.unshift(nodeToMove) // Move to beginning if at end
      }
      break
    case 'top':
      newNodes.unshift(nodeToMove)
      break
    case 'bottom':
      newNodes.push(nodeToMove)
      break
  }

  return newNodes
}

/**
 * Sorts a codeframe's children so that Base nodes appear first, followed by Code/Net/Arith nodes.
 * Preserves the relative order within each group.
 */
export function sortCodeframeChildren(codeframe: GenNode): GenNode {
  if (!codeframe.children || codeframe.children.length === 0) {
    return codeframe
  }

  const sortedChildren = [...codeframe.children].sort((a, b) => {
    const aIsBase = a.type === 'Base'
    const bIsBase = b.type === 'Base'

    // If both are bases or both are not bases, maintain original order
    if (aIsBase === bIsBase) {
      return 0
    }

    // Bases come before non-bases
    return aIsBase ? -1 : 1
  })

  return {
    ...codeframe,
    children: sortedChildren
  }
}

/**
 * Creates a deep copy of a node with new IDs
 */
export function duplicateNode(node: GenNode): GenNode {
  const duplicatedNode: GenNode = {
    ...node,
    id: generateNodeId(),
    children: node.children ? node.children.map(child => duplicateNode(child)) : undefined
  }

  // Update child parent IDs
  if (duplicatedNode.children) {
    duplicatedNode.children.forEach(child => {
      child.parentId = duplicatedNode.id
    })
  }

  return duplicatedNode
}

/**
 * Adds base nodes to a codeframe
 */
export function addBasesToCodeframe(codeframe: GenNode, baseTypes: string[]): GenNode {
  const baseNodes: GenNode[] = baseTypes.map(baseType => {
    const baseInfo = getBaseTypeInfo(baseType)
    return {
      type: 'Base',
      id: generateNodeId(),
      parentId: codeframe.id,
      isExpanded: false,
      isFolder: false,
      value1: baseInfo.value1,
      value2: baseInfo.value2,
      level: (codeframe.level || 1) + 1,
      anyChildren: false,
      meta: [{ key: 'Base', value: baseType }]
    }
  })

  const codeframeWithBases = {
    ...codeframe,
    children: [...(codeframe.children || []), ...baseNodes]
  }

  // Sort children so bases appear at the top
  return sortCodeframeChildren(codeframeWithBases)
}

/**
 * Gets the list of base types that are already present in a codeframe
 */
export function getExistingBaseTypes(codeframe: GenNode): string[] {
  if (!codeframe.children) return []

  return codeframe.children
    .filter(child => child.type === 'Base')
    .map(base => {
      // Server-loaded bases store type in value1, manually-created bases have it in both value1 and meta
      // Use value1 as the primary source since it's consistent across both
      return base.value1 || ''
    })
    .filter(Boolean) as string[]
}

/**
 * Removes base nodes of specific types from a codeframe
 */
export function removeBasesFromCodeframe(codeframe: GenNode, baseTypesToRemove: string[]): GenNode {
  if (!codeframe.children || baseTypesToRemove.length === 0) {
    return codeframe
  }

  const filteredChildren = codeframe.children.filter(child => {
    // Keep non-Base children
    if (child.type !== 'Base') return true

    // For Base children, check if their type should be removed
    // Use value1 as the primary source (works for both server-loaded and manually-created bases)
    const baseType = child.value1 || ''

    // Keep the base if it's NOT in the removal list
    return !baseTypesToRemove.includes(baseType)
  })

  const codeframeWithoutBases = {
    ...codeframe,
    children: filteredChildren
  }

  // Sort children to maintain bases at the top
  return sortCodeframeChildren(codeframeWithoutBases)
}

/**
 * Gets display information for base types
 */
function getBaseTypeInfo(baseType: string): { value1: string; value2: string } {
  const baseTypeMap: Record<string, { value1: string; value2: string }> = {
    'cuf': { value1: 'cuf', value2: 'Unweighted Total' },
    'cwf': { value1: 'cwf', value2: 'Total' },
    'ebf': { value1: 'ebf', value2: 'Effective Base' }
  }

  return baseTypeMap[baseType] || { value1: baseType, value2: baseType }
}

/**
 * Finds a node by its ID in a tree structure
 */
export function findNodeById(nodes: GenNode[], id: number): GenNode | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node
    }
    if (node.children) {
      const found = findNodeById(node.children, id)
      if (found) return found
    }
  }
  return null
}

/**
 * Gets the index of a node in an array by its ID
 */
export function getNodeIndex(nodes: GenNode[], nodeId: number): number {
  return nodes.findIndex(node => node.id === nodeId)
}

/**
 * Removes a node from an array by its ID
 */
export function removeNodeById(nodes: GenNode[], nodeId: number): GenNode[] {
  return nodes.filter(node => node.id !== nodeId)
}

/**
 * Updates a node in an array by replacing it with a new version
 */
export function updateNode(nodes: GenNode[], updatedNode: GenNode): GenNode[] {
  return nodes.map(node => node.id === updatedNode.id ? updatedNode : node)
}

/**
 * Gets all selected codes from a variable node based on checked state
 */
export function getSelectedCodesFromVariable(variableNode: GenNode, checkedIds: string[]): GenNode[] {
  if (!variableNode.children) return []
  
  return variableNode.children.filter(child => 
    child.type === 'Code' && checkedIds.includes(`${child.id}/${child.value1}`)
  )
}

/**
 * Checks if all codes in a variable are selected
 */
export function areAllCodesSelected(variableNode: GenNode, checkedIds: string[]): boolean {
  if (!variableNode.children) return false
  
  const codeChildren = variableNode.children.filter(child => child.type === 'Code')
  if (codeChildren.length === 0) return false
  
  return codeChildren.every(child => checkedIds.includes(`${child.id}/${child.value1}`))
}

/**
 * Copies a node to the bottom of its sibling group. Top-level nodes are
 * appended to the end of the axis; nested nodes are appended to the end
 * of their parent's `children`, so a copy from inside a filter stays
 * inside that filter.
 *
 * The clone gets fresh ids throughout (via `duplicateNode`). Because the
 * clone is appended as a sibling of the source, it inherits the source's
 * `parentId` and `level` — exactly what the surrounding siblings already
 * carry, regardless of whether those values match the live tree structure
 * (the syntax-to-nodes loader emits stale `parentId`s for nested items).
 *
 * Mirrors the recursive visit pattern used by `handleRemove` in
 * `AxisEditor`, which handles nested nodes reliably.
 *
 * Returns a new tree. If `sourceNode` isn't found, returns `nodes`
 * unchanged (by reference) so consumers can no-op on miss.
 */
export function copyNodeInTree(nodes: GenNode[], sourceNode: GenNode): GenNode[] {
  const visit = (arr: GenNode[]): [GenNode[], boolean] => {
    const idx = arr.findIndex(n => n.id === sourceNode.id)
    if (idx !== -1) {
      return [[...arr, duplicateNode(arr[idx])], true]
    }
    let changed = false
    const next = arr.map(n => {
      if (!n.children || n.children.length === 0) return n
      const [childList, did] = visit(n.children)
      if (did) {
        changed = true
        return { ...n, children: childList }
      }
      return n
    })
    return [next, changed]
  }

  const [out, wasFound] = visit(nodes)
  return wasFound ? out : nodes
}
export type AxisMoveDirection = 'up' | 'down' | 'top' | 'bottom'

/**
 * Moves an axis item within its own sibling group, at any depth, as the
 * item menu's Move does (AxisEditor). Up on the first item and Down on the
 * last leave it where it is. Returns a new tree; an id that is not on the
 * axis leaves the items as they were.
 */
export function reorderAxisNode(nodes: GenNode[], targetId: number, direction: AxisMoveDirection): GenNode[] {
  const visit = (arr: GenNode[]): [GenNode[], boolean] => {
    const idx = arr.findIndex(n => n.id === targetId)
    if (idx !== -1) {
      const copy = [...arr]
      const [item] = copy.splice(idx, 1)
      switch (direction) {
        case 'up':
          copy.splice(Math.max(0, idx - 1), 0, item)
          break
        case 'down':
          copy.splice(Math.min(arr.length - 1, idx + 1), 0, item)
          break
        case 'top':
          copy.unshift(item)
          break
        case 'bottom':
          copy.push(item)
          break
      }
      return [copy, true]
    }
    let changed = false
    const next = arr.map(n => {
      if (n.children && n.children.length > 0) {
        const [childList, did] = visit(n.children)
        if (did) {
          changed = true
          return { ...n, children: childList }
        }
      }
      return n
    })
    return [next, changed]
  }
  const [out] = visit(nodes)
  return out
}

/**
 * Removes an axis item at any depth, as the item menu's Remove does
 * (AxisEditor). Returns a new tree; an id that is not on the axis leaves the
 * items as they were.
 */
export function removeAxisNode(nodes: GenNode[], targetId: number): GenNode[] {
  const visit = (arr: GenNode[]): [GenNode[], boolean] => {
    const idx = arr.findIndex(n => n.id === targetId)
    if (idx !== -1) {
      const copy = [...arr]
      copy.splice(idx, 1)
      return [copy, true]
    }
    let changed = false
    const next = arr.map(n => {
      if (n.children && n.children.length > 0) {
        const [childList, did] = visit(n.children)
        if (did) {
          changed = true
          return { ...n, children: childList }
        }
      }
      return n
    })
    return [next, changed]
  }
  const [out] = visit(nodes)
  return out
}
